import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { v4 as uuidv4 } from 'uuid';
import { RecipeService } from '../recipe.service';
import { Recipe } from '../recipe.model';

@Component({
    selector: 'app-recipe-form',
    templateUrl: './recipe-form.component.html',
    styleUrls: ['./recipe-form.component.scss'],
    standalone: false
})
export class RecipeFormComponent implements OnInit {
  @Input() recipe: Recipe | null = null;
  @Output() saved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  form: Partial<Recipe> & { feelings: string[]; ingredients: string[] } = {
    feelings: [],
    ingredients: [],
  };
  feelingInput = '';
  ingredientInput = '';
  formError = '';
  saving = false;

  private currentUser: { uid: string; displayName: string | null; email: string | null } | null = null;

  constructor(
    private recipeService: RecipeService,
    private afAuth: AngularFireAuth
  ) {}

  ngOnInit() {
    this.afAuth.currentUser.then(user => { this.currentUser = user; });
    if (this.recipe) {
      this.form = {
        ...this.recipe,
        feelings: [...this.recipe.feelings],
        ingredients: [...this.recipe.ingredients],
      };
    }
  }

  get isEditMode(): boolean {
    return !!this.recipe;
  }

  // Feeling chip input
  onFeelingKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.addFeeling();
    }
  }

  addFeeling() {
    const val = this.feelingInput.trim().toLowerCase();
    if (val && !this.form.feelings.includes(val)) {
      this.form.feelings = [...this.form.feelings, val];
    }
    this.feelingInput = '';
  }

  removeFeeling(tag: string) {
    this.form.feelings = this.form.feelings.filter(f => f !== tag);
  }

  // Ingredient chip input
  onIngredientKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.addIngredient();
    }
  }

  addIngredient() {
    const val = this.ingredientInput.trim().toLowerCase();
    if (val && !this.form.ingredients.includes(val)) {
      this.form.ingredients = [...this.form.ingredients, val];
    }
    this.ingredientInput = '';
  }

  removeIngredient(tag: string) {
    this.form.ingredients = this.form.ingredients.filter(i => i !== tag);
  }

  save() {
    this.formError = '';
    if (!this.form.name?.trim()) {
      this.formError = 'Name is required.';
      return;
    }

    // Flush any pending chip inputs
    if (this.feelingInput.trim()) this.addFeeling();
    if (this.ingredientInput.trim()) this.addIngredient();

    const recipe: Recipe = {
      id: this.recipe?.id || uuidv4(),
      name: this.form.name.trim(),
      cuisine: this.form.cuisine?.trim() || '',
      feelings: this.form.feelings,
      ingredients: this.form.ingredients,
      addedBy: this.recipe?.addedBy ||
        (this.currentUser?.displayName || this.currentUser?.email || 'Unknown'),
      addedByUid: this.recipe?.addedByUid || this.currentUser?.uid || '',
      createdAt: this.recipe?.createdAt || Date.now(),
      ...(this.form.notes?.trim() && { notes: this.form.notes.trim() }),
      ...(this.form.link?.trim() && { link: this.form.link.trim() }),
    };

    this.saving = true;
    const promise = this.recipe
      ? this.recipeService.updateRecipe(recipe)
      : this.recipeService.addRecipe(recipe);

    promise.then(() => {
      this.saving = false;
      this.saved.emit();
    }).catch(() => {
      this.formError = 'Failed to save. Please try again.';
      this.saving = false;
    });
  }

  cancel() {
    this.cancelled.emit();
  }
}
